// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// director.ts — the opposition, tiered BY POLICY and never by stat inflation.
//
// The tier is chosen by the heat BAND, which means the difficulty of the building is something the
// player drives rather than something a menu sets. There is no "hard mode" in this game; there is a
// player who has been loud.
//
//   CALM      pure schedule. Fixed loops of length 4/6/8/12, lenses on period 8, lcm <= 24, so the
//             whole world is periodic with period 24 and every next-turn arc can be drawn dashed
//             HONESTLY. Previewability at CALM is not a UI affordance, it is a true statement.
//   RESTLESS  reactive. Each body scores the live marks and din orders it knows about and takes the
//             best one. Still one target at a time, still legible.
//   SWEEP     territorial. The floor splits into three zones and one body is pinned to each, refusing
//             to leave it. This tier produces the game's best trap: the corridor BETWEEN two zones is
//             emptier than it was at CALM, so a player who deliberately pushes heat to fifty has
//             discovered that the escalation is a tool.
//   LOCKDOWN  converging. Reinforcements come in at the FRONT and walk at the last known position,
//             which drains bodies toward the entrance and empties the back — at the price of sealing
//             the exit you came in by.
//
// Everything here is deterministic and integer. Ties break on the lowest cell index, always, because
// a shared seed that resolves differently on two devices is not a shared building.

import { Band, CELLS, SCUFF_LIFE, Mark } from './tuning';
import { canStep, chebyshev, neighbours, stepToward, walkField, type Door, type Grid } from './map';
import { MState, band, logOrder, type Minder, type State } from './rules';

/**
 * Split the walkable floor into three zones by multi-source BFS from the anchors.
 * Nearest anchor wins; an exact tie goes to the lower anchor index, so the partition is total.
 */
export function sweepZones(g: Grid, doors: readonly Door[], anchors: readonly number[]): Int8Array {
  const zone = new Int8Array(CELLS).fill(-1);
  const dist = new Int16Array(CELLS).fill(-1);
  const q: number[] = [];
  for (let a = 0; a < anchors.length; a++) {
    const c = anchors[a];
    if (!canStep(g, doors, c) || dist[c] >= 0) continue;
    dist[c] = 0;
    zone[c] = a;
    q.push(c);
  }
  const nb: number[] = [];
  for (let h = 0; h < q.length; h++) {
    const c = q[h];
    neighbours(c, nb);
    for (const n of nb) {
      if (!canStep(g, doors, n)) continue;
      const nd = dist[c] + 1;
      if (dist[n] === -1) {
        dist[n] = nd;
        zone[n] = zone[c];
        q.push(n);
      } else if (nd === dist[n] && zone[c] < zone[n]) {
        zone[n] = zone[c];
      }
    }
  }
  return zone;
}

/** One step along a patrol loop. Reaching a waypoint advances to the next one. */
export function patrolStep(s: State, m: Minder): number {
  if (m.loop.length === 0) return -1;
  if (m.cell === m.loop[m.loopPos]) m.loopPos = (m.loopPos + 1) % m.loop.length;
  const want = m.loop[m.loopPos];
  if (want === m.cell) return -1;
  return stepToward(s.grid, s.doors, m.cell, want);
}

/**
 * Give this body its orders for the coming turn.
 *
 * Called once per minder at the end of every turn, AFTER marks have been read — so a read that just
 * turned a patrol into a chase is not immediately overwritten by this.
 */
export function orderFor(s: State, m: Minder): void {
  const tier = band(s);

  // A chase outranks everything until the trail is cold: he walks to where you were and then, finding
  // nothing, downgrades to a search of that spot rather than magically re-acquiring you.
  if (m.state === MState.Chase) {
    if (m.target >= 0 && m.cell !== m.target) return;
    m.state = MState.Investigate;
    return;
  }

  if (tier >= Band.Sweep && !m.reinforcement) {
    sweepOrder(s, m);
    return;
  }

  if (tier >= Band.Restless) {
    const t = bestLead(s, m);
    if (t >= 0) {
      m.state = MState.Investigate;
      m.target = t;
      logOrder(s, m.id, 'investigate', t, 'lead');
      return;
    }
  }

  // CALM, or nothing to chase: back to the schedule.
  if (m.state === MState.Investigate && m.cell === m.target) {
    m.state = MState.Patrol;
    m.target = -1;
  }
}

/**
 * RESTLESS: score every live mark this body could plausibly walk to and take the best.
 *
 * recency * 2 + 20 - pathDistance. A fresh mark across the floor loses to a stale one next door, which
 * is what makes leaving a trail near a patrol route worse than leaving one far from it — and therefore
 * makes WHERE you are loud a decision rather than just WHETHER.
 */
function bestLead(s: State, m: Minder): number {
  const field = walkField(s.grid, s.doors, m.cell);
  let best = -1;
  let bestScore = -1 << 20;
  for (const k of s.marks) {
    if (k.kind === Mark.Scuff && s.turn - k.born >= SCUFF_LIFE) continue;
    const d = field[k.cell];
    if (d < 0) continue;
    const recency = Math.max(0, SCUFF_LIFE - (s.turn - k.born));
    const score = recency * 2 + 20 - d;
    if (score > bestScore || (score === bestScore && k.cell < best)) {
      bestScore = score;
      best = k.cell;
    }
  }
  return bestScore > 0 ? best : -1;
}

/**
 * SWEEP: grid-search your own zone and do not leave it.
 *
 * The refusal to cross a boundary is the whole point and it is not an oversight — it is what makes the
 * seam between two zones quieter under a full search than it was under a calm patrol.
 */
function sweepOrder(s: State, m: Minder): void {
  const zones = sweepZones(s.grid, s.doors, s.b.anchors);
  const field = walkField(s.grid, s.doors, m.cell);
  let best = -1;
  let bestD = 1 << 20;
  for (let c = 0; c < CELLS; c++) {
    if (zones[c] !== m.zone || s.searched[c]) continue;
    if (!canStep(s.grid, s.doors, c)) continue;
    const d = field[c];
    if (d < 0) continue;
    if (d < bestD || (d === bestD && c < best)) {
      bestD = d;
      best = c;
    }
  }
  if (best < 0) {
    // Zone fully walked: wipe the slate and go round again. A search that finishes and stops is a
    // stopped system, and mechanism invariant LIVENESS is what would otherwise pass on it silently.
    for (let c = 0; c < CELLS; c++) if (zones[c] === m.zone) s.searched[c] = 0;
    return;
  }
  m.state = MState.Investigate;
  m.target = best;
  logOrder(s, m.id, 'investigate', best, 'sweep');
}

/**
 * How exposed is this cell to the bodies on the floor right now, as a cheap scalar the bot and the
 * generator's interest metrics both use. Not part of the rules — nothing branches on it.
 */
export function pressure(s: State, cell: number): number {
  let p = 0;
  for (const m of s.minders) {
    const d = chebyshev(m.cell, cell);
    if (d <= 6) p += 7 - d;
  }
  return p;
}
